/**
 * Inline icon set.
 *
 * Inline rather than an icon package: the designs use a single 1.6px-stroke line style, the app
 * needs perhaps twenty glyphs, and every one of them must inherit `currentColor` so the same
 * glyph works on the white table and on the navy sidebar. A dependency would ship hundreds.
 */
import type { SVGProps } from 'react'

export interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number
}

function Svg({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const MailIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </Svg>
)

export const EyeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
    <circle cx="12" cy="12" r="2.8" />
  </Svg>
)

export const EyeOffIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.6 6.1A9.6 9.6 0 0 1 12 6c6.4 0 10 6 10 6a17.6 17.6 0 0 1-3.2 3.9M6.3 7.7A17.4 17.4 0 0 0 2 12s3.6 6 10 6a9.7 9.7 0 0 0 4-.85" />
    <path d="m9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </Svg>
)

export const ArrowRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 12h15" />
    <path d="m13 6 6 6-6 6" />
  </Svg>
)

export const ArrowLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 12H5" />
    <path d="m11 6-6 6 6 6" />
  </Svg>
)

export const UsersIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" />
    <circle cx="10" cy="8" r="3.2" />
    <path d="M20 19v-1.4a3.5 3.5 0 0 0-2.6-3.35" />
    <path d="M15.6 5.2a3.2 3.2 0 0 1 0 5.6" />
  </Svg>
)

export const UserPlusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-4A3.5 3.5 0 0 0 3 17.5V19" />
    <circle cx="8.5" cy="8" r="3.2" />
    <path d="M18 8v6M21 11h-6" />
  </Svg>
)

export const ChartIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20V10" />
    <path d="M10 20V4" />
    <path d="M16 20v-7" />
    <path d="M21 20H3" />
  </Svg>
)

export const IdCardIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.2" />
    <circle cx="8.5" cy="11" r="2.1" />
    <path d="M5.4 16.2a3.4 3.4 0 0 1 6.2 0" />
    <path d="M14.5 10h4M14.5 13.5h4" />
  </Svg>
)

export const SettingsIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.6a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.87 1.2v.18a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-2.93-1.16l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0-1.2-2.87H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.25 6.7l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 2.87-1.2V2.5a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 2.87 1.2l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0 1.2 2.87h.18a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.54 1.11Z" />
  </Svg>
)

export const MonitorIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="4" width="19" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </Svg>
)

export const LogoutIcon = (p: IconProps) => (
  <Svg {...p}>
    {/* The door is on the left and the arrow leaves through it — an arrow pointing *into* a box
        on the right reads as "sign in", which is the opposite of what this control does. */}
    <path d="M10 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20H10" />
    <path d="M16 16l4-4-4-4" />
    <path d="M20 12H9" />
  </Svg>
)

export const SearchIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Svg>
)

export const PlusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

/** The two-slider glyph the designs use in every sortable column header. */
export const SortIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={1.5}>
    <path d="M7 4v9M7 16v4" />
    <path d="M17 4v4M17 11v9" />
    <circle cx="7" cy="14.5" r="1.9" />
    <circle cx="17" cy="9.5" r="1.9" />
  </Svg>
)

export const MoreIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={2.2}>
    <circle cx="5" cy="12" r="0.6" />
    <circle cx="12" cy="12" r="0.6" />
    <circle cx="19" cy="12" r="0.6" />
  </Svg>
)

export const PencilIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="m14.5 6 3 3" />
  </Svg>
)

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16" />
    <path d="M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7" />
    <path d="M6.5 7l.8 12a1.6 1.6 0 0 0 1.6 1.5h6.2a1.6 1.6 0 0 0 1.6-1.5l.8-12" />
  </Svg>
)

export const SendInviteIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 13.5V18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4.5" />
    <path d="M3 13.5 5.6 6a2 2 0 0 1 1.9-1.4h9a2 2 0 0 1 1.9 1.4L21 13.5" />
    <path d="M3 13.5h5l1 2h6l1-2h5" />
  </Svg>
)

export const DownloadIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5v11" />
    <path d="m7.5 10 4.5 4.5 4.5-4.5" />
    <path d="M4.5 19.5h15" />
  </Svg>
)

export const UploadCloudIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.5 18a4.5 4.5 0 0 1-.6-8.96 6 6 0 0 1 11.6-1.1A4.25 4.25 0 0 1 18.5 18" />
    <path d="M12 21V12" />
    <path d="m8.8 15.2 3.2-3.2 3.2 3.2" />
  </Svg>
)

export const CheckIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={2.4}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Svg>
)

export const AlertTriangleIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4.2 2.8 19.2a1.4 1.4 0 0 0 1.2 2.1h16a1.4 1.4 0 0 0 1.2-2.1Z" />
    <path d="M12 10v4.2" />
    <circle cx="12" cy="17.6" r="0.5" fill="currentColor" />
  </Svg>
)

export const InfoIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11.5V16" />
    <circle cx="12" cy="8.3" r="0.6" fill="currentColor" />
  </Svg>
)

export const CloseIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
)

export const PlayIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={1.9}>
    <path d="M8 5.5v13l11-6.5-11-6.5Z" />
  </Svg>
)

export const StopIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
  </Svg>
)

export const RefreshIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 11a8 8 0 0 0-13.7-5.1L3.5 8.5" />
    <path d="M4 13a8 8 0 0 0 13.7 5.1l2.8-2.6" />
    <path d="M3.5 4v4.5H8M20.5 20v-4.5H16" />
  </Svg>
)

export const ChevronLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m14.5 6-6 6 6 6" />
  </Svg>
)

export const ChevronRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9.5 6 6 6-6 6" />
  </Svg>
)

export const ChevronsLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m11.5 6-6 6 6 6M18.5 6l-6 6 6 6" />
  </Svg>
)

export const ChevronsRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12.5 6 6 6-6 6M5.5 6l6 6-6 6" />
  </Svg>
)

export const ChevronDownIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Svg>
)

export const TextIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={1.5}>
    <path d="M4 18 8 6l4 12M5.4 14.5h5.2" />
    <path d="M20 10.4v7.6M20 12c-2.6 0-4 1.1-4 3s1.4 2.8 4 2.4" />
  </Svg>
)

export const StatusIcon = (p: IconProps) => (
  <Svg {...p} strokeWidth={1.6}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
  </Svg>
)

export const ReportIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5Z" />
    <path d="M14 3.5v5h5" />
    <path d="M9 13h6M9 16.5h4" />
  </Svg>
)

export const FolderIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.9a1.5 1.5 0 0 1 1.2.6l1 1.4H19a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 19 18.5H5A1.5 1.5 0 0 1 3.5 17Z" />
  </Svg>
)

export const GlobeIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.2 2.4 3.4 5.4 3.4 8.5s-1.2 6.1-3.4 8.5c-2.2-2.4-3.4-5.4-3.4-8.5S9.8 5.9 12 3.5Z" />
  </Svg>
)

export const CopyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M15 5.5A1.5 1.5 0 0 0 13.5 4H5.5A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15" />
  </Svg>
)

export const KeyIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="8" cy="15" r="3.5" />
    <path d="m10.6 12.6 6.6-6.6" />
    <path d="m15.2 8 2.2 2.2" />
    <path d="m17.6 5.6 2.4 2.4" />
  </Svg>
)


export const FileCsvIcon = ({ size = 34, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" {...rest}>
    <rect x="2" y="2" width="36" height="36" rx="8" fill="#e8f7ef" />
    <path
      d="M22 9h-8a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V16Z"
      fill="#0fb769"
      opacity="0.16"
    />
    <path
      d="M22 9h-8a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V16Zm0 0v7h7"
      stroke="#0a8f52"
      strokeWidth="1.7"
      fill="none"
      strokeLinejoin="round"
    />
    <text x="20" y="27" textAnchor="middle" fontSize="8" fontWeight="700" fill="#0a8f52">
      CSV
    </text>
  </svg>
)

/** Product mark used in the admin sidebar and the participant top bar. */
export const SparkMarkIcon = ({ size = 26, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" {...rest}>
    <path
      d="M12 3.2a8.8 8.8 0 1 0 8.5 6.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
    <path
      d="M18.6 2.4l.85 2.1 2.1.85-2.1.85-.85 2.1-.85-2.1-2.1-.85 2.1-.85Z"
      fill="currentColor"
    />
  </svg>
)

/** The illustration on the participant welcome card. */
export const RecordSessionIcon = ({ size = 76, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 76 76" aria-hidden="true" {...rest}>
    <g fill="none" stroke="var(--navy-900)" strokeWidth="2.6" strokeLinejoin="round">
      <rect x="10" y="12" width="50" height="38" rx="4" />
      <path d="M4 58h62l-4-8H8Z" strokeLinecap="round" />
      <rect x="18" y="20" width="20" height="14" rx="2.5" />
      <path d="M44 21.5 52 26v6l-8 4.5Z" />
      <path d="M18 42h24" strokeLinecap="round" />
    </g>
    <circle cx="26" cy="42" r="4" fill="var(--navy-900)" />
    <circle cx="52" cy="16" r="6.5" fill="var(--lime-400)" />
  </svg>
)
